import { call, put, select, take } from 'redux-saga/effects';

import { ChallengesAPI } from 'services/challenge.api';
import { MediaAPI } from 'services/media.api';
import {
  userChallenges,
  userChallenge,
  userChallengeDeclarations,
  setUserChallengesLoadingStatus,
  setChallengeLoadingStatus,
  postUserParticipation,
  getTotalUsersParticipation,
  challengeResults,
  setChallengeResultsStatus,
  challengeRanks,
  setChallengeRanksLoading,
  setChallengeResultVotingStatus,
  challengeResultVote,
  challengeResultUnvote,
  postUserChallengePhotoResults,
  setSelectedChallengeDataAction,
  USER_CHALLENGES,
  USER_CHALLENGE_DECLARATIONS,
  setLoadingSelectedChallengeDataAction,
} from 'actions/challenge.actions';
import { ActionType, IChallengeList } from 'interfaces';
import { CHALLENGE, RESULT_STATUS } from 'constants/challenges';
import { LOADING_STATUS } from 'constants/status';
import { checkIfChallengeIsInChallengeList } from 'pages/HistoryOfDeclaredChallenges/utils';
import {
  challengeSelector,
  sortedDeclaredChallengesSelector,
} from 'reducers/challenge.reducer';
import { content } from 'actions/content.actions';
import { ContentAPI } from 'services';
import {
  WIDGET_GLOBAL_VIEW_CHALLENGES,
  widgetGlobalViewChallenges,
} from 'actions/widget.actions';

// Get user-declared challenges from the redux-store
function* fetchUserChallenges() {
  try {
    // Target the userChallenges action to get user-declared challenges from the redux-store
    yield put(userChallenges.request());

    // Wait for the userChallenges request to be processed and the redux-store to be updated
    yield take([USER_CHALLENGES.SUCCESS, USER_CHALLENGES.FAILURE]);

    // Get user-declared challenges from the redux-store
    const sortedDeclaredChallenges: IChallengeList[] = yield select(
      sortedDeclaredChallengesSelector,
    );
    return sortedDeclaredChallenges;
  } catch (error) {
    console.error(
      'History page: Error when getting user-declared challenges',
      error,
    );
    return [];
  }
}

export function* setSelectedChallengeDataSaga({
  payload,
}: ActionType): Generator {
  const { selectedChallengeId, isMobile } = payload;

  try {
    yield put(setLoadingSelectedChallengeDataAction(LOADING_STATUS.PENDING));
    // Get user-declared challenges from the redux-store
    const declaredChallenges: any = yield call(fetchUserChallenges);

    const defaultDesktopSelectedChallengeId =
      !isMobile &&
      selectedChallengeId === undefined &&
      declaredChallenges[0].id;

    const finalSelectedChallengeId =
      !isMobile && selectedChallengeId === undefined
        ? defaultDesktopSelectedChallengeId
        : selectedChallengeId;

    // Check if the selected challenge is in user-declared challenges list
    const isInChallengeList = checkIfChallengeIsInChallengeList(
      +finalSelectedChallengeId,
      declaredChallenges,
    );

    if (!isInChallengeList) {
      yield put(
        setSelectedChallengeDataAction.success({
          historySelectedChallenge: {
            selectedChallengeId: finalSelectedChallengeId,
            isInDeclaredChallengesList: isInChallengeList,
            data: null,
          },
        }),
      );
      yield put(setLoadingSelectedChallengeDataAction(LOADING_STATUS.RESOLVED));
    }

    // If is not already existing, put the selected challenge declarations in the redux-store
    yield put(
      userChallengeDeclarations.request({
        challengeId: finalSelectedChallengeId,
      }),
    );

    // Wait for the userChallengeDeclarations request to be processed and the redux-store to be updated
    yield take([
      USER_CHALLENGE_DECLARATIONS.SUCCESS,
      USER_CHALLENGE_DECLARATIONS.FAILURE,
    ]);

    // Get the selected challenge data
    const { data: selectedChallengeData }: any = yield select(
      challengeSelector(finalSelectedChallengeId),
    );

    yield put(
      setSelectedChallengeDataAction.success({
        historySelectedChallenge: {
          selectedChallengeId: finalSelectedChallengeId,
          isInDeclaredChallengesList: isInChallengeList,
          data: selectedChallengeData,
        },
      }),
    );

    yield put(setLoadingSelectedChallengeDataAction(LOADING_STATUS.RESOLVED));
  } catch (error) {
    console.error(
      "History page: Error when getting user-declared challenges and the selected challenge'data",
      error,
    );
    yield put(setSelectedChallengeDataAction.failure({ error }));
    yield put(setLoadingSelectedChallengeDataAction(LOADING_STATUS.REJECTED));
  }
}

// wait the finish of all sagas that change the challenge status before resolve
const setChallengeFetchStatus = (() => {
  let nWaitingUsersByChallengeId: { [key: string]: number } = {};
  return {
    setPendingStatus: function* (challengeId: string) {
      if (nWaitingUsersByChallengeId[challengeId] === undefined) {
        nWaitingUsersByChallengeId[challengeId] = 0;
      }
      nWaitingUsersByChallengeId[challengeId]++;
      yield put(setChallengeLoadingStatus(challengeId, LOADING_STATUS.PENDING));
    },
    setResolvedStatus: function* (challengeId: string) {
      if (--nWaitingUsersByChallengeId[challengeId] === 0) {
        yield put(
          setChallengeLoadingStatus(challengeId, LOADING_STATUS.RESOLVED),
        );
      }
    },
    setRejectedStatus: function* (challengeId: string) {
      yield put(
        setChallengeLoadingStatus(challengeId, LOADING_STATUS.REJECTED),
      );
    },
  };
})();

export function* getUserChallengesSaga(): unknown {
  try {
    yield put(setUserChallengesLoadingStatus(LOADING_STATUS.PENDING));
    const { data } = yield call(ChallengesAPI.getUserChallenges);
    yield put(userChallenges.success({ data }));
    yield put(setUserChallengesLoadingStatus(LOADING_STATUS.RESOLVED));
  } catch (error) {
    yield put(userChallenges.failure({ error }));
  }
}

export function* getUserChallengeSaga({ payload }: ActionType): unknown {
  const { challengeId } = payload;

  try {
    yield setChallengeFetchStatus.setPendingStatus(challengeId);
    const { data } = yield call(ChallengesAPI.getUserChallenge, challengeId);
    const isGoalMecanicByProduct = data?.isRewardingGlobal !== undefined && !data?.isRewardingGlobal;
    try {
      if (data?.type === CHALLENGE.LIST && isGoalMecanicByProduct) {
        const { data: allChallengeListUsersResult } = yield call(
          ChallengesAPI.getChallengeListResults,
          challengeId,
        );
        data.allChallengeListUsersResult = allChallengeListUsersResult;
      }
    } catch (error) {
      console.error(error);
    }
    yield put(userChallenge.success({ challengeId, data }));
    yield setChallengeFetchStatus.setResolvedStatus(challengeId);
  } catch (error) {
    yield put(userChallenge.failure({ challengeId, error }));
    yield setChallengeFetchStatus.setRejectedStatus(challengeId);
  }
}

export function* getUserChallengeDeclarationsSaga({
  payload,
}: ActionType): unknown {
  const { challengeId } = payload;

  try {
    yield setChallengeFetchStatus.setPendingStatus(challengeId);
    const { data } = yield call(
      ChallengesAPI.getUserChallengeDeclarations,
      challengeId,
    );
    yield put(userChallengeDeclarations.success({ challengeId, data }));
    yield setChallengeFetchStatus.setResolvedStatus(challengeId);
  } catch (error) {
    yield put(userChallenge.failure({ challengeId, error }));
    yield setChallengeFetchStatus.setRejectedStatus(challengeId);
  }
}

export function* getUserChallengeDeclarationSaga({
  payload,
}: ActionType): unknown {
  const { challengeId } = payload;

  try {
    yield setChallengeFetchStatus.setPendingStatus(challengeId);
    const { data } = yield call(
      ChallengesAPI.getUserChallengeDeclaration,
      challengeId,
    );
    yield put(userChallenge.success({ challengeId, data }));
    yield setChallengeFetchStatus.setResolvedStatus(challengeId);
  } catch (error) {
    yield put(userChallenge.failure({ challengeId, error }));
    yield setChallengeFetchStatus.setRejectedStatus(challengeId);
  }
}

export function* getTotalUsersParticipationSaga({
  payload,
}: ActionType): unknown {
  const { challenge } = payload;

  try {
    yield put(setChallengeLoadingStatus(challenge.id, LOADING_STATUS.PENDING));
    const { data: totalParticipatingUsers } = yield call(
      challenge['@type'] === `Challenge${CHALLENGE.DEAL}`
        ? ChallengesAPI.getTotalUsersParticipation
        : ChallengesAPI.getTotalChallengeParticipants,
      challenge.id,
    );
    yield put(
      getTotalUsersParticipation.success({
        challengeId: challenge.id,
        totalParticipatingUsers,
      }),
    );
    yield put(setChallengeLoadingStatus(challenge.id, LOADING_STATUS.RESOLVED));
  } catch (error) {
    yield put(
      getTotalUsersParticipation.failure({ challengeId: challenge.id, error }),
    );
    yield put(setChallengeLoadingStatus(challenge.id, LOADING_STATUS.REJECTED));
  }
}

export function* postUserParticipationSaga({ payload }: ActionType): unknown {
  const {
    challengeId,
    isParticipating,
    contentId,
    currentGlobalViewWidgetSet,
  } = payload;
  // /!\ be carefful: this saga is triggered by clicking on actions buttons
  // in the details page + the content page 
  // for all widgets types, including global view widgets
  const apiValue = { isParticipating };
  try {
    yield setChallengeFetchStatus.setPendingStatus(challengeId);
    const { data } = yield call(
      ChallengesAPI.postUserParticipation,
      challengeId,
      apiValue,
    );
    if (currentGlobalViewWidgetSet) {
      yield put(widgetGlobalViewChallenges.request(currentGlobalViewWidgetSet));
      yield take([WIDGET_GLOBAL_VIEW_CHALLENGES.SUCCESS]);
    }
    yield put(postUserParticipation.success({ challengeId, data }));
    if (contentId) {
      const { data: contents } = yield call(ContentAPI.getContent, contentId);
      yield put(content.success({ id: contentId, content: contents }));
    }
    yield setChallengeFetchStatus.setResolvedStatus(challengeId);
  } catch (error) {
    yield put(postUserParticipation.failure({ challengeId, error }));
    yield setChallengeFetchStatus.setRejectedStatus(challengeId);
  }
}

export function* getChallengeResultsSaga({ payload }: ActionType): unknown {
  const { challengeId, page, order, challengeType, all } = payload;
  const isFirstCall = page === 1;

  try {
    yield put(
      setChallengeResultsStatus(
        challengeId,
        LOADING_STATUS.PENDING,
        isFirstCall,
      ),
    );
    const { data } = yield call(ChallengesAPI.getChallengeResults, {
      challengeId,
      page,
      order,
      challengeType,
      isFetchingAll: all,
    });
    let isWaitingModeration = false;
    if (!data['hydra:totalItems'] && challengeType !== CHALLENGE.PHOTO) {
      const moderation = yield call(
        ChallengesAPI.getChallengeResultsModeration,
        challengeId,
      );
      isWaitingModeration = moderation.data;
    }
    yield put(
      challengeResults.success({
        challengeId,
        data: data['hydra:member'],
        page,
        totalItems: data['hydra:totalItems'],
        isWaitingModeration,
      }),
    );
    yield put(
      setChallengeResultsStatus(
        challengeId,
        LOADING_STATUS.RESOLVED,
        isFirstCall,
      ),
    );
  } catch (error) {
    yield put(challengeResults.failure({ challengeId, error }));
    yield put(
      setChallengeResultsStatus(
        challengeId,
        LOADING_STATUS.REJECTED,
        isFirstCall,
      ),
    );
  }
}

export function* getChallengeRanksSaga({ payload }: ActionType): unknown {
  const { challengeId } = payload;

  try {
    yield put(setChallengeRanksLoading(true));
    const { data } = yield call(ChallengesAPI.getChallengeRanks, challengeId);
    yield put(challengeRanks.success({ challengeId, data }));
    yield put(setChallengeRanksLoading(false));
  } catch (error) {
    yield put(challengeRanks.failure({ challengeId, error }));
    yield put(setChallengeRanksLoading(false));
  }
}

export function* postUserChallengePhotoResultsSaga({
  payload,
}: ActionType): unknown {
  const { challengeId, photo, status, resultId } = payload;
  try {
    yield put(
      setChallengeResultsStatus(challengeId, LOADING_STATUS.PENDING, null),
    );
    if (status === RESULT_STATUS.REFUSED && resultId) {
      yield call(ChallengesAPI.deleteChallengeResult, resultId);
    }
    const { data: postMediaResponse } = yield call(
      MediaAPI.postMedia,
      photo,
      'ChallengeResult',
    );
    const { data } = yield call(
      ChallengesAPI.postUserChallengePhotoResults,
      challengeId,
      postMediaResponse.id,
    );
    yield put(postUserChallengePhotoResults.success({ challengeId, data }));
    yield put(
      challengeResults.request({
        challengeId,
        order: 'asc',
        challengeType: CHALLENGE.PHOTO,
      }),
    );
    yield put(
      setChallengeResultsStatus(challengeId, LOADING_STATUS.RESOLVED, null),
    );
  } catch (error) {
    yield put(postUserChallengePhotoResults.failure({ challengeId, error }));
    yield put(
      setChallengeResultsStatus(challengeId, LOADING_STATUS.REJECTED, null),
    );
  }
}

export function* getChallengeResultVoteSaga({ payload }: ActionType): unknown {
  const { resultId, callback } = payload;

  try {
    yield put(setChallengeResultVotingStatus(resultId, LOADING_STATUS.PENDING));
    const { data } = yield call(ChallengesAPI.getChallengeResultVote, resultId);
    if (callback) {
      yield put(callback());
    }
    yield put(challengeResultVote.success({ resultId, data }));
    yield put(
      setChallengeResultVotingStatus(resultId, LOADING_STATUS.RESOLVED),
    );
  } catch (error) {
    yield put(challengeResultVote.failure({ resultId, error }));
    yield put(
      setChallengeResultVotingStatus(resultId, LOADING_STATUS.REJECTED),
    );
  }
}

export function* getChallengeResultUnvoteSaga({
  payload,
}: ActionType): unknown {
  const { resultId, callback } = payload;

  try {
    yield put(setChallengeResultVotingStatus(resultId, LOADING_STATUS.PENDING));
    const { data } = yield call(
      ChallengesAPI.getChallengeResultUnvote,
      resultId,
    );
    if (callback) {
      yield put(callback());
    }
    yield put(challengeResultUnvote.success({ resultId, data }));
    yield put(
      setChallengeResultVotingStatus(resultId, LOADING_STATUS.RESOLVED),
    );
  } catch (error) {
    yield put(challengeResultUnvote.failure({ resultId, error }));
    yield put(
      setChallengeResultVotingStatus(resultId, LOADING_STATUS.REJECTED),
    );
  }
}
